
import SBtnEsia from '@app/shared/src/components/ui/Button/SBtnEsia.vue';
import { storeToRefs } from 'pinia';
import { computed, defineComponent, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import TheAttentionBar from 'SharedComponents/TheAttentionBar.vue';
import HeaderNotification from 'SharedComponents/header/Notification.vue';
import HeaderProfile from 'SharedComponents/header/Profile.vue';
import { HeaderProfileLink } from 'SharedComponents/header/profile.d';
import { isProfileOrganization } from 'SharedDir/common/composables/profile/useProfileForm';
import { useDeviceSize } from 'SharedDir/common/composables/useDeviceSize';
import { useFeatureFlags } from 'SharedDir/common/composables/useFeatureFlags';
import {
  PORTAL_CERTIFICATES_ROUTE,
  useMyCertificatesAccess,
} from 'SharedDir/common/composables/useMyCertificatesAccess';
import {
  HEADER_HEIGHT_PORTAL_WITH_TEST_MODE_BANNER,
  TOOLBAR_HEIGHT_PORTAL_WITH_TEST_MODE_BANNER,
} from 'SharedDir/constants/attention-bar-options';
import { FeatureFlagName } from 'SharedDir/declarations/system/region-customization';
import TheChat from 'SharedDir/modules/Chats/TheChat.vue';
import { useAuthStore } from 'SharedDir/stores/auth.store';
import { useChatStore } from 'SharedDir/stores/chat.store';
import { useNotificationStore } from 'SharedDir/stores/notification.store';
import { useUserStore } from 'SharedDir/stores/user.store';

import { useLoadingSsr } from 'PortalDir/common/composables/useLoadingSsr';
import { REGION_ENVIRONMENT, TEST_MODE_BANNER } from 'PortalDir/constants/app/common';
import ServiceSidebar from 'PortalDir/modules/Services/components/ServiceSidebar.vue';
import { routerInitialPath, routerMainPageName } from 'PortalDir/router/router.constants';
import { useAppStore } from 'stores/app';

export default defineComponent({
  name: 'TheHeader',
  components: {
    TheAttentionBar,
    ServiceSidebar,
    HeaderProfile,
    SBtnEsia,
    TheChat,
    HeaderNotification,
  },
  methods: {},
  setup() {
    const appStore = useAppStore();
    const authStore = useAuthStore();
    const userStore = useUserStore();

    const { isLoading } = useLoadingSsr();

    const isAuthenticated = computed(() => authStore.isAuthenticated);
    const notificationStore = useNotificationStore();

    const { collapseNotificationModal } = storeToRefs(notificationStore);
    const showServiceSidebar = ref(false);
    const showMenuSidebar = ref(false);

    const { showMyCertificates } = useMyCertificatesAccess();

    const linkListProfile = computed(
      () =>
        [
          {
            icon: 'app:user-multipleusers',
            name: 'Сменить профиль пользователя',
            route: 'profile-dialog',
          },
          { icon: 'app:user-mini', name: 'Профиль пользователя', route: 'profile' },
          { icon: 'app:text-edit', name: 'Мои заявления', route: 'profile-issues' },
          showMyCertificates.value
            ? {
                icon: 'app:keep-the-shape-7',
                name: 'Мои сертификаты',
                route: PORTAL_CERTIFICATES_ROUTE,
              }
            : null,
          { icon: 'app:message', name: 'Уведомления', route: 'profile-notifications' },
          { icon: 'app:telephone', name: 'Контактная информация', route: 'contacts' },
          // { icon: 'app:support', name: 'Написать в поддержку', route: 'ui-kit' },
          { icon: 'app:login', name: 'Выйти из системы', route: 'exit' },
        ].filter((item) => item !== null) as HeaderProfileLink[]
    );

    const profileLabel = computed(() => {
      if (userStore.fio.length < 20) {
        return userStore.fio;
      }
      return userStore.fullLastNameWithInitials;
    });

    const isOrganization = computed((): boolean => {
      return isProfileOrganization(userStore.currentProfile);
    });

    const organizationType = computed((): string => {
      if (isLoading.value) {
        return '';
      }
      if (isOrganization.value && userStore?.currentProfile?.name) {
        return `Организация: ${userStore.currentProfile.name}`;
      }
      return isOrganization.value ? 'Организация' : 'Физическое лицо';
    });

    const router = useRouter();

    const changeVisibilityNotificationModal = () => {
      notificationStore.setNotificationModal(!collapseNotificationModal.value);
    };

    async function routeTo(routeName) {
      if (routeName === 'exit') {
        await authStore.logout();
        await router.push({ name: 'main' });
      } else if (routeName === 'profile-dialog') {
        appStore.setProfileDialog(true);
      } else if (routeName === 'notification') {
        changeVisibilityNotificationModal();
      } else {
        router.push({ name: routeName });
      }
    }
    function goToServices() {
      return { name: 'services' };
    }

    function goToNews() {
      return { name: 'news' };
    }

    function goToUsefulLinks() {
      return { name: 'useful-links' };
    }

    function goToLanguageCenter() {
      return { name: 'language-center' };
    }

    onMounted(async () => {
      isLoading.value = false;

      if (isAuthenticated.value) {
        notificationStore.fetchUnreadNotifications();
      }
    });

    const viewAttentionBar = TEST_MODE_BANNER;

    //  Новая ширина хедера с учетом банера
    const viewAttentionBarHeaderStyle = computed(() =>
      viewAttentionBar ? `height: ${HEADER_HEIGHT_PORTAL_WITH_TEST_MODE_BANNER};` : ''
    );

    //  Новая ширина toolbar с учетом банера
    const viewAttentionBarToolbarStyle = computed(() =>
      viewAttentionBar ? `height: ${TOOLBAR_HEIGHT_PORTAL_WITH_TEST_MODE_BANNER};` : ''
    );

    const chatStore = useChatStore();
    const { showChat } = storeToRefs(chatStore);

    const isVisibleChannelsExplainKmns = useFeatureFlags().getFeatureFlag(
      FeatureFlagName.isVisibleChannelsExplainKmns
    );

    const isVisibleLanguageCenter = useFeatureFlags().getFeatureFlag(
      FeatureFlagName.IsVisibleLinksToEthnoportalInPortal
    );

    const goToTgChannelExplainKmns = () => {
      return 'https://t.me/tC_Y0M04YWZi';
    };

    const goToMaxChannelExplainKmns = () => {
      return 'https://max.ru/join/-aw1Hd6_eY5QE4L0B-Ixb6kG3GJqNTbsI7RaiZCjMCA';
    };

    const linkList = computed(() => {
      const defaultLinks: Array<{
        title: string;
        type: 'router' | 'external' | 'menu';
        to: () => any;
      }> = [
        {
          title: 'Меры поддержки',
          type: 'router',
          to: goToServices,
        },
        {
          title: 'Новости',
          type: 'router',
          to: goToNews,
        },
        {
          title: 'Полезные ссылки',
          type: 'router',
          to: goToUsefulLinks,
        },
      ];

      if (isVisibleLanguageCenter.value) {
        defaultLinks.push({
          title: 'Языковой центр',
          type: 'router',
          to: goToLanguageCenter,
        });
      }

      if (isVisibleChannelsExplainKmns.value) {
        defaultLinks.push({
          title: 'Объясняем.КМНС',
          type: 'menu',
          to: () => [
            {
              label: 'Max',
              url: goToMaxChannelExplainKmns(),
              icon: `${routerInitialPath}icons/custom/messengers/max.png`,
            },
            {
              label: 'Telegram',
              url: goToTgChannelExplainKmns(),
              icon: `${routerInitialPath}icons/custom/messengers/telegram.webp`,
            },
          ],
        });
      }
      return defaultLinks;
    });

    return {
      ...useDeviceSize(),
      viewAttentionBar,
      appStore,
      routerInitialPath,
      linkListProfile,
      linkList,
      profileLabel,
      isAuthenticated,
      routerMainPageName,
      routeTo,
      changeVisibilityNotificationModal,
      showServiceSidebar,
      showMenuSidebar,
      isLoading,
      openChat: () => chatStore.setShowChat(!showChat.value),
      organizationType,
      viewAttentionBarHeaderStyle,
      viewAttentionBarToolbarStyle,
      REGION_ENVIRONMENT,
    };
  },
});
